import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Heart, ChevronRight } from "lucide-react";

/**
 * Rotating preview of real Quiet Wall entries — used beside the Donate form
 * and anywhere the marketing site wants to show community memory before
 * asking for anything. No amounts, no counts. Soft fade between entries.
 *
 * Resilient: if the API call fails or returns zero entries, falls back to
 * the SEED entries the backend already returns so the panel is never empty.
 */

const ROTATE_MS = 9000;

const FALLBACK = [
  { honoree_name: "her grandmother", dedication_note: "She made it through, quietly, with no one watching." },
  { honoree_name: "myself, at 22",  dedication_note: "You were not crazy. You were trying to survive." },
  { honoree_name: "the next woman to find this", dedication_note: "You are not alone." },
];

export default function QuietWallPreview({ compact = false }) {
  const [entries, setEntries] = useState(FALLBACK);
  const [idx, setIdx] = useState(0);
  const [fadeIn, setFadeIn] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/donations/wall");
        const items = Array.isArray(data?.items) ? data.items : [];
        if (!cancelled && items.length > 0) setEntries(items.slice(0, 12));
      } catch { /* keep fallback */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const t = setInterval(() => {
      // Fade out → swap → fade in. Keeps the transition gentle.
      setFadeIn(false);
      setTimeout(() => {
        setIdx((i) => (i + 1) % entries.length);
        setFadeIn(true);
      }, 500);
    }, ROTATE_MS);
    return () => clearInterval(t);
  }, [entries.length]);

  const cur = entries[idx] || entries[0];

  return (
    <aside
      data-testid="quiet-wall-preview"
      className={`rounded-3xl bg-brand-soft/40 border border-brand/20 ${
        compact ? "p-5" : "p-6 sm:p-8"
      }`}
    >
      <div className="flex items-center gap-2">
        <Heart className="w-4 h-4 text-brand" />
        <p className="font-script text-xl text-brand">held in honor</p>
      </div>
      <h3 className="mt-1 font-serif text-2xl leading-snug">Messages left by people who gave.</h3>
      <p className="mt-2 text-sm text-foreground/65 leading-relaxed">
        No amounts. No counts. Just memory.
      </p>

      <div
        key={idx}
        className={`mt-5 rounded-2xl bg-card border border-border p-5 transition-opacity duration-500 ${
          fadeIn ? "opacity-100" : "opacity-0"
        }`}
        data-testid="quiet-wall-preview-card"
      >
        <p className="text-xs uppercase tracking-widest text-foreground/55">In honor of</p>
        <p className="mt-1 font-serif text-lg italic text-foreground">{cur.honoree_name || "a beloved one"}</p>
        {cur.dedication_note && (
          <p className="mt-3 font-serif italic text-foreground/85 leading-relaxed">&ldquo;{cur.dedication_note}&rdquo;</p>
        )}
      </div>

      <Link
        to="/wall"
        data-testid="quiet-wall-preview-see-all"
        className="mt-4 inline-flex items-center gap-1 text-sm text-brand hover:underline"
      >
        Visit the Quiet Wall <ChevronRight className="w-3 h-3" />
      </Link>
    </aside>
  );
}
